import React, { useEffect } from 'react';
import type { FlexProps } from '@withjoy/joykit';
import { Flex, TextV2 } from '@withjoy/joykit';
import { JoyAiV2 } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { AiBorderBox } from '@shared/components/AiBorderBox';
import { ShimmerAnimation } from '@shared/components/ShimmerAnimation';
import { useUserConsent } from '@shared/utils/userConsent';
import type { AutoPopulationPollingStatus } from '@shared/utils/accommodations/autoPopulation/types';
import { useAutoPopulationWithDataRefetch } from '../../hooks/useAutoPopulationWithDataRefetch';

type AsyncAutoPopulationStatusProps = Pick<FlexProps, 'marginX' | 'marginBottom' | 'margin' | 'padding'> &
  Readonly<{
    statusOverride?: AutoPopulationPollingStatus;
  }>;

export const AsyncAutoPopulationStatus = React.memo(({ statusOverride, ...props }: AsyncAutoPopulationStatusProps) => {
  const { tq } = useTranslation('autoPopulation');

  const { status: pollStatus, results } = useAutoPopulationWithDataRefetch({
    mode: 'poll-only',
    context: 'AsyncAutoPopulationStatus',
  });

  // Only PENDING is honored from the override — it bridges the gap between the caller
  // starting auto-population and the poll cycle discovering the operation. Terminal states
  // (COMPLETED, FAILED) are always driven by the poll to ensure data is refetched first.
  const status = statusOverride === 'PENDING' ? 'PENDING' : pollStatus;

  // Mark recommendations as seen when auto-population completes with results.
  // This consent key gates dashboard nudges and the AI feedback dialog.
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  useEffect(() => {
    if (loadingUserConsent || pollStatus !== 'COMPLETED' || results.length === 0) return;
    if (!getUserConsent('hasSeenAccommodationsRecommendations')) {
      setUserConsent('hasSeenAccommodationsRecommendations', true);
    }
  }, [loadingUserConsent, pollStatus, results.length, getUserConsent, setUserConsent]);

  if (status !== 'PENDING') return null;

  return (
    <Flex
      {...props}
      alignItems="center"
      role="status"
      aria-live="polite"
    >
      <AiBorderBox
        padding={6}
        background="white"
        borderRadius={12}
      >
        <ShimmerAnimation enabled={true}>
          <Flex gap={3}>
            <JoyAiV2 size={48} />
            <TextV2
              typographyVariant={'hed1'}
              textAlign="left"
            >
              {tq`Finding hotels near your venue with exclusive event rates, up to 40% off.`}
            </TextV2>
          </Flex>
        </ShimmerAnimation>
      </AiBorderBox>
    </Flex>
  );
});
